import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Config } from "../../../../common/interfaces/config";
import defaultContent from "../../../../content/performanceIncomeDashboard";
import commonContent from "../../../../content/common";
import { AWARD_PROGRESS } from "../../../../common/enums/award-progress-codes";
import { QUALIFICATION_STATUS } from "../../../../common/enums/incentive-enums";
import { PIN_LEVEL_CODE } from "../../../../common/enums/pin-level-codes";
import { getRoutePathForAwardCode, getNavLabelForAwardCode } from "../../../../common/util/Navigation/navigationUtil";
import { parseStatus } from "../AnnualPerformanceIncome/utils";
import { useAppSelector } from "../../../../store";
import { getCanonicalTitle } from "../../../../common/util/Incentives/IncentiveRegistry";
import type { NavDropdown } from "../../../../common/interfaces/routing";
import type { IncomeTileConfig } from "./IncomeBreakdown.types";
import type {
  IncomeBreakdownApiResponse,
  IncomeBreakdownIncentive,
  IncomeBreakdownMetric,
  IncomeBreakdownRequirement,
} from "./IncomeBreakdown.api.types";

const CORE_BONUS_PARTICIPANT_AWARD_CODES = new Set([
  PIN_LEVEL_CODE.EMERALD_PARTICIPANT,
  PIN_LEVEL_CODE.DIAMOND_PARTICIPANT,
  PIN_LEVEL_CODE.DIAMOND_PLUS_PARTICIPANT,
]);

const ANNUAL_CORE_PLAN_AWARD_CODES = new Set([
  ...CORE_BONUS_PARTICIPANT_AWARD_CODES,
  PIN_LEVEL_CODE.EMERALD,
  PIN_LEVEL_CODE.DIAMOND,
  PIN_LEVEL_CODE.DIAMOND_PLUS,
]);

type PVResult = { percent: number; met: boolean; label: string };
type KPIResult = { percent: number; met: boolean; label: string };
type PinResult = { met: boolean; label: string; value?: string; statusTag?: AWARD_PROGRESS; status: AWARD_PROGRESS };

function getIncentiveContent(incentiveLabel: string): Record<string, string> {
  return (defaultContent.incentives as Record<string, Record<string, string>>)[incentiveLabel] ?? {};
}

// Backend label values that don't have their own content bucket in
// performanceIncomeDashboard.ts's `incentives` object — requirement/status content for these
// lives under a shared underlying label instead (e.g. "pggAnnual" reuses "pgg"'s requirement
// content — the backend's requirement ContentKeys, like "maintainGrowPQs", are unprefixed and
// only exist under "pgg"). Labels not listed here use their own label as-is.
const CONTENT_LABEL_OVERRIDES: Record<string, string> = {
  pggAnnual: "pgg",
};

function translateRequirement(
  req: IncomeBreakdownRequirement,
  t: TFunction,
  incentiveLabel: string,
): PVResult | KPIResult | PinResult {
  const fullContentKey = req.contentKey.includes(".") ? req.contentKey : `${incentiveLabel}.${req.contentKey}`;
  const [labelPart, ...keyParts] = fullContentKey.split(".");
  const actualKey = keyParts.join(".");
  const incentiveContent = getIncentiveContent(labelPart);
  const label = String(
    t(`incentives.${fullContentKey}`, {
      defaultValue: incentiveContent[actualKey] ?? req.contentKey,
      current: req.current ?? "",
      target: req.target ?? "",
      bonusAmount: req.target ?? "",
      ...(req.targetWithLeg ? { targetWithLeg: req.targetWithLeg } : {}),
      ...(req.perfYear !== undefined ? { perfYear: String(req.perfYear) } : {}),
      ...(req.minPQs !== undefined ? { minPQs: String(req.minPQs) } : {}),
    }),
  );

  if (req.type === "PVRequirement" || req.type === "KPI" || req.type === "LegProgressKPI") {
    return { percent: req.percent, met: req.met, label };
  }

  const pinResult: PinResult = {
    met: req.met,
    label,
    status: parseStatus(req.status ?? ""),
  };

  if (req.isRequirementWithAmount) {
    pinResult.value = req.current;
  }

  if (req.isRequirementWithStatus && !req.isRequirementWithAmount) {
    pinResult.statusTag = parseStatus(req.status ?? "");
  }

  return pinResult;
}

function mapMetricToLegsProgressItem(
  metric: IncomeBreakdownMetric,
  t: TFunction,
): { label: string; value: string; delta?: number } {
  const legsProgressContent = defaultContent.legsProgress as Record<string, string>;
  const label = String(
    t(`legsProgress.${metric.contentKey}`, {
      defaultValue: legsProgressContent[metric.contentKey] ?? metric.contentKey,
    }),
  );
  const value = metric.qualificationType
    ? String(
        t(`legsProgress.${metric.qualificationType}`, {
          defaultValue: legsProgressContent[metric.qualificationType],
          value: metric.formatted,
        }),
      )
    : metric.formatted;
  const item: { label: string; value: string; delta?: number } = { label, value };
  if (metric.moMTrend) {
    item.delta = metric.moMTrend.difference;
  }
  return item;
}

function mapIncentiveToTile(
  incentive: IncomeBreakdownIncentive,
  t: TFunction,
  symbol: string,
  selectedPeriod: string,
  configuration: Config | undefined,
  isoCountryCode: string,
  aff: string,
  menuConfig?: NavDropdown[],
): IncomeTileConfig {
  const {
    label: originalLabel,
    overrideLabel,
    amount,
    status,
    perfYear,
    statusMessageKey,
    requirements,
    awardCode,
    metrics,
    showStatusOnHeader,
    hideHeaderAmount,
    hasOnlyHeader,
    hideStatus,
    isComingSoon,
  } = incentive;
  const contentLabel = CONTENT_LABEL_OVERRIDES[originalLabel] ?? originalLabel;
  const titleContent = getIncentiveContent(contentLabel);
  const isCorePlanBonus = ANNUAL_CORE_PLAN_AWARD_CODES.has(awardCode);
  const navigationIdentity =
    menuConfig && awardCode ? (getNavLabelForAwardCode(menuConfig, awardCode) ?? originalLabel) : originalLabel;
  // Core Plan Diamond Bonus shares the `diamondBonus` content key with the A70 100+ Diamond Bonus.
  // Resolve Core Plan titles from their dashboard content so their fixed disclaimer marker remains `*`.
  const tileLabel = isCorePlanBonus
    ? String(t(`incentives.${contentLabel}.title`, { defaultValue: titleContent.title ?? originalLabel }))
    : getCanonicalTitle({ id: navigationIdentity, contentKey: navigationIdentity, awardCode }, t, {
        selectedPeriod,
        configuration,
        isoCountryCode,
        aff,
        symbol,
      });
  const label = overrideLabel ?? contentLabel;
  const incentiveContent = getIncentiveContent(label);

  const isFoster = label === "foster";
  const link = !isFoster && menuConfig ? getRoutePathForAwardCode(menuConfig, awardCode) : undefined;

  const getHeaderTag = () => {
    if (!showStatusOnHeader) return undefined;
    if (CORE_BONUS_PARTICIPANT_AWARD_CODES.has(awardCode) && status === QUALIFICATION_STATUS.QUALIFIED) {
      return AWARD_PROGRESS.QUALIFIED;
    }
    return parseStatus(status);
  };

  const header: IncomeTileConfig["header"] = {
    label: tileLabel,
    value: isComingSoon
      ? String(t("notAvailable", { defaultValue: commonContent.notAvailable as string, ns: "common" }))
      : showStatusOnHeader || hideHeaderAmount
        ? undefined
        : amount.Formatted,
    link: link || undefined,
    showArrow: !!link,
    tag: getHeaderTag(),
    labelTestId: `income-tile-label-${originalLabel}`,
  };

  if (isComingSoon) {
    return {
      header,
      comingSoon: {
        title: String(t("incentives.comingSoon.title", { defaultValue: defaultContent.incentives.comingSoon.title })),
        message: String(
          t("incentives.comingSoon.message", { defaultValue: defaultContent.incentives.comingSoon.message }),
        ),
      },
    };
  }

  const tile: IncomeTileConfig = { header };

  if (hasOnlyHeader) {
    return tile;
  }

  // Map metrics array to legsProgress — skip if status message will be shown
  if (!statusMessageKey && metrics && metrics.length > 0) {
    const items = metrics.map((metric) => mapMetricToLegsProgressItem(metric, t));
    tile.legsProgress = { items };
  }

  if (!requirements || requirements.length === 0) {
    if (!showStatusOnHeader && !hideStatus) {
      tile.statusBadge = parseStatus(status);
    }
    // Show a status message when the API provides a statusMessageKey (e.g. "denied", "held", "manuallyApproved").
    // First tries a per-incentive override (e.g. incentives.pgg.denied), then falls back to the shared
    // statusMessages object (incentives.statusMessages.denied) with {title} and {perfYear} interpolation.
    // Override the badge to "Not Eligible" when the API signals a denial via statusMessageKey.
    // Useful for special cases like Health and Wellbeing where Not Qualified are treated as Not Eligible
    if (statusMessageKey === "denied") {
      tile.statusBadge = AWARD_PROGRESS.NOT_ELIGIBLE;
    }
    if (statusMessageKey) {
      const perIncentiveContent = titleContent[statusMessageKey];
      const sharedMessages = defaultContent.incentives.statusMessages as Record<string, string> | undefined;
      const fallback = perIncentiveContent ?? sharedMessages?.[statusMessageKey] ?? "";
      const i18nKey = perIncentiveContent
        ? `incentives.${contentLabel}.${statusMessageKey}`
        : `incentives.statusMessages.${statusMessageKey}`;
      tile.statusMessage = String(
        t(i18nKey, {
          defaultValue: fallback,
          title: tileLabel,
          ...(perfYear !== undefined ? { perfYear: String(perfYear) } : {}),
        }),
      );
    }
    return tile;
  }

  const pvRequirements = requirements.filter((r) => r.type === "PVRequirement");
  const allKpiRequirements = requirements.filter((r) => r.type === "KPI");
  const pinRequirements = requirements.filter((r) => r.type === "PinRequirement");
  const legProgressKPIRequirements = requirements.filter((r) => r.type === "LegProgressKPI");

  // Foster: pull leadershipPv and leadershipPercent KPIs into a metricCard
  const FOSTER_METRIC_KEYS = new Set(["foster.leadershipPv", "foster.leadershipPercent"]);
  const metricCardKpis = isFoster ? allKpiRequirements.filter((r) => FOSTER_METRIC_KEYS.has(r.contentKey)) : [];
  const kpiRequirements = isFoster
    ? allKpiRequirements.filter((r) => !FOSTER_METRIC_KEYS.has(r.contentKey))
    : allKpiRequirements;

  if (metricCardKpis.length > 0) {
    tile.metricCard = {
      items: metricCardKpis.map((r) => {
        const key = r.contentKey.split(".").pop() ?? r.contentKey;
        const fallback = incentiveContent[key] ?? r.contentKey;
        const translatedLabel = String(t(`incentives.${r.contentKey}`, { defaultValue: fallback }));
        return { label: translatedLabel, value: r.current ?? "--" };
      }),
    };
  }

  if (pvRequirements.length === 1) {
    const mapped = translateRequirement(pvRequirements[0], t, label) as PVResult;
    tile.pvRequirement = { percent: mapped.percent, met: mapped.met, label: mapped.label };
  } else if (pvRequirements.length > 1) {
    tile.pvRequirements = pvRequirements.map((r) => translateRequirement(r, t, label) as PVResult);
  }

  if (kpiRequirements.length > 0) {
    tile.kpiTrackers = {
      items: kpiRequirements.map((r) => {
        const mapped = translateRequirement(r, t, label) as KPIResult;
        if (r.variant === "legs") {
          return {
            label: mapped.label,
            achievedSegments: r.achievedSegments ?? 0,
            totalSegments: r.totalSegments ?? 0,
            isMet: mapped.met,
            variant: "legs" as const,
          };
        }
        return {
          label: mapped.label,
          percentage: mapped.percent,
          isMet: mapped.met,
          variant: "progress" as const,
        };
      }),
    };
  }

  if (pinRequirements.length === 1) {
    tile.pinRequirement = translateRequirement(pinRequirements[0], t, label) as PinResult;
  } else if (pinRequirements.length > 1) {
    tile.pinRequirements = pinRequirements.map((r) => translateRequirement(r, t, label) as PinResult);
  }

  if (legProgressKPIRequirements.length > 0) {
    tile.legProgressKPI = {
      items: legProgressKPIRequirements.map((r) => {
        const mapped = translateRequirement(r, t, label) as KPIResult;
        return {
          label: mapped.label,
          achievedSegments: r.achievedSegments ?? 0,
          totalSegments: r.totalSegments ?? 0,
          isMet: mapped.met,
        };
      }),
    };
  }

  if (!hideStatus) {
    tile.statusBadge = parseStatus(status);
  }

  return tile;
}

export interface IncomeBreakdownTiles {
  incentiveTiles: IncomeTileConfig[];
  estimatedIncomeValue: string;
  hasAnnualCorePlanBonuses?: boolean;
}

export function useIncomeBreakdownTiles(
  apiResponse: IncomeBreakdownApiResponse | undefined,
  menuConfig?: NavDropdown[],
): IncomeBreakdownTiles {
  const { t } = useTranslation(["performanceIncomeDashboard", "navigation", "common"]);
  const user = useAppSelector((state) => state.boot?.user);
  const configuration = useAppSelector((state) => state.boot?.configuration);
  const { selectedPerformanceYear } = useAppSelector((state) => state.period);

  const symbol = String(t("disclaimerSymbol", { defaultValue: commonContent.disclaimerSymbol, ns: "common" }));

  if (!apiResponse || !apiResponse.incentives) {
    return {
      incentiveTiles: [],
      estimatedIncomeValue: apiResponse?.totalEstimatedIncome?.Formatted ?? "",
    };
  }

  const incentiveTiles = apiResponse.incentives.map((incentive) => {
    const tile = mapIncentiveToTile(
      incentive,
      t,
      symbol,
      selectedPerformanceYear,
      configuration,
      user.isoCountryCode,
      user.aff,
      menuConfig,
    );
    if (tile.header) {
      tile.header.tealiumTag = incentive.label;
    }
    return tile;
  });

  const estimatedIncomeValue = apiResponse.totalEstimatedIncome.Formatted;

  const hasAnnualCorePlanBonuses = apiResponse.incentives.some((incentive) =>
    ANNUAL_CORE_PLAN_AWARD_CODES.has(incentive.awardCode),
  );

  return {
    incentiveTiles,
    estimatedIncomeValue,
    hasAnnualCorePlanBonuses,
  };
}
